export type GlobalCollectionId = Record<string, never>;

export type AccountCollectionId = {
  accountId: string;
};

export type SpaceCollectionId = AccountCollectionId & {
  spaceId: string;
};

export type EventCollectionId = SpaceCollectionId & {
  eventId: string;
  version?: number;
};

export const getAccountPath = (id: AccountCollectionId) =>
  `accounts/${id.accountId}`;

export const getSpacePath = (id: SpaceCollectionId) =>
  `${getAccountPath(id)}/spaces/${id.spaceId}`;

export const getEventPath = (id: EventCollectionId) =>
  `${getAccountPath(id)}/events/${id.eventId}`;

/** Content scope — learning path (authoring) or event snapshot (runtime). */
export type ContentScopeId = SpaceCollectionId | EventCollectionId;

export const isEventContentScope = (
  id: ContentScopeId
): id is EventCollectionId => "eventId" in id && !!id.eventId;

export const getContentBasePath = (id: ContentScopeId): string => {
  if (isEventContentScope(id)) {
    const eventPath = getEventPath(id);
    return id.version
      ? `${eventPath}/contentReleases/${id.version}`
      : eventPath;
  }
  return getSpacePath(id);
};

export const toSpaceId = (id: EventCollectionId): SpaceCollectionId => ({
  accountId: id.accountId,
  spaceId: id.spaceId,
});

export const toAccountId = (
  id: SpaceCollectionId | EventCollectionId
): AccountCollectionId => ({
  accountId: id.accountId,
});

export const formatEventCollectionId = (id: EventCollectionId): string =>
  `${id.accountId}:${id.spaceId}:${id.eventId}`;

export const parseEventCollectionId = (
  key: string
): EventCollectionId | null => {
  const [accountId, spaceId, eventId] = key.split(":");
  if (!accountId || !spaceId || !eventId) {
    return null;
  }
  return { accountId, spaceId, eventId };
};

export const hasAccountId = (
  id: AccountCollectionId | undefined
): id is AccountCollectionId => !!id?.accountId;

export const hasSpaceId = (
  id: SpaceCollectionId | undefined
): id is SpaceCollectionId => !!id?.accountId && !!id?.spaceId;
